// watch/VideoSettingsDialog.tsx — "Video settings" for your own video (1.27.60): who can
// watch (owner ruling: account → Your channel → each video, most specific choice wins, videos
// only), what viewers may do, and captions (VideoCaptionsSection — those apply straight away).
// A video posted to specific people or regions keeps that list unless you pick another
// choice here; editing the list itself stays in the composer.
import { useState } from 'react';
import styled from 'styled-components';
import { Button, Dialog, Switch, useToast } from '@/ui';
import { Icon } from '@/icons';
import type { IconName } from '@/icons';
import { useSession } from '@/hooks';
import { postsService } from '@/core/services/postsService';
import type { AudienceType, Post } from '@/core/models/post';
import { channelDefaultLabel } from '@/features/home/composer/channelDefault';
import { VideoCaptionsSection } from './VideoCaptionsSection';

const Form = styled.form`
  display: grid;
  gap: 16px;
  width: 100%;
  h3 {
    margin: 0;
    font: 700 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
    color: ${({ theme }) => theme.color.textMuted};
  }
`;

const Options = styled.div`
  display: grid;
  gap: 6px;
`;

const Option = styled.label`
  display: grid;
  grid-template-columns: auto auto 1fr;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.surface};
  cursor: pointer;
  font: 600 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textPrimary};
  &:hover {
    background: ${({ theme }) => theme.color.hover};
  }
  &:has(input:checked) {
    background: ${({ theme }) => theme.color.surfaceRaised};
  }
  input {
    accent-color: ${({ theme }) => theme.color.accentCyan};
  }
  small {
    display: block;
    font-weight: 500;
    color: ${({ theme }) => theme.color.textMuted};
  }
`;

const Permission = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textPrimary};
  small {
    display: block;
    color: ${({ theme }) => theme.color.textMuted};
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

type Choice = 'everyone' | 'public' | 'followers' | 'unlisted' | 'keep';

type PermissionKey = 'videoAllowDownload' | 'videoAllowEmbed' | 'videoAllowClips';

const PERMISSIONS: { key: PermissionKey; label: string; hint: string }[] = [
  { key: 'videoAllowDownload', label: 'Allow downloads', hint: 'Viewers can save the video file' },
  {
    key: 'videoAllowEmbed',
    label: 'Allow embedding',
    hint: 'Other sites can show the video in their pages',
  },
  {
    key: 'videoAllowClips',
    label: 'Allow clips',
    hint: 'People can share a short part of it, with a link back to yours. Turning this off also stops clips already made.',
  },
];

const KEEP_LABEL: Partial<Record<AudienceType, string>> = {
  regions: 'Specific regions',
  people: 'Specific people',
};

export function VideoSettingsDialog({ post, onClose }: { post: Post; onClose: () => void }) {
  const { currentUser } = useSession();
  const toast = useToast();
  const video = post.video!;
  const current = post.audience?.type ?? 'everyone';
  const keepable = current === 'regions' || current === 'people';
  const [choice, setChoice] = useState<Choice>(keepable ? 'keep' : (current as Choice));
  const [perms, setPerms] = useState<Record<PermissionKey, boolean>>({
    videoAllowDownload: video.allowDownload ?? false,
    videoAllowEmbed: video.allowEmbed ?? true,
    videoAllowClips: video.allowClips ?? true,
  });
  const [saving, setSaving] = useState(false);

  const options: { value: Choice; icon: IconName; label: string; hint: string }[] = [
    ...(keepable
      ? [
          {
            value: 'keep' as const,
            icon: (current === 'people' ? 'user-check' : 'map-pin') as IconName,
            label: KEEP_LABEL[current] ?? '',
            hint: 'Keep the list you posted it with',
          },
        ]
      : []),
    {
      value: 'everyone',
      icon: 'video',
      label: 'Channel default',
      hint: `${channelDefaultLabel(currentUser)}. Change it in Settings → Privacy`,
    },
    {
      value: 'public',
      icon: 'globe',
      label: 'Public',
      hint: 'Anyone, even if your account is private',
    },
    { value: 'followers', icon: 'users', label: 'Followers', hint: 'Only people who follow you' },
    {
      value: 'unlisted',
      icon: 'link',
      label: 'Unlisted',
      hint: 'Only people with the link; never listed or sent to followers',
    },
  ];

  async function save() {
    setSaving(true);
    try {
      await postsService.videoSettings(post.id, {
        ...(choice === 'keep' ? {} : { audience: choice }),
        ...perms,
      });
      toast('Video settings saved');
      onClose();
    } catch {
      toast('Something went wrong');
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} title="Video settings">
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <h3 id="video-visibility">Who can watch</h3>
        <Options role="radiogroup" aria-labelledby="video-visibility">
          {options.map((o) => (
            <Option key={o.value}>
              <input
                type="radio"
                name="video-visibility"
                value={o.value}
                checked={choice === o.value}
                onChange={() => setChoice(o.value)}
              />
              <Icon name={o.icon} size={16} />
              <span>
                {o.label}
                <small>{o.hint}</small>
              </span>
            </Option>
          ))}
        </Options>
        <h3>What viewers may do</h3>
        {PERMISSIONS.map((p) => (
          <Permission key={p.key}>
            <span>
              {p.label}
              <small>{p.hint}</small>
            </span>
            <Switch
              label={p.label}
              checked={perms[p.key]}
              onChange={(v) => setPerms((prev) => ({ ...prev, [p.key]: v }))}
            />
          </Permission>
        ))}
        <h3>Captions</h3>
        <VideoCaptionsSection videoId={video.id} />
        <Footer>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            Save
          </Button>
        </Footer>
      </Form>
    </Dialog>
  );
}
